import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { X, RotateCcw, Download, Box } from "lucide-react";
import { buildScene, defaultCamera, renderScene } from "./scene3d";

const DEG = Math.PI / 180;
const btn = (active) => ({
  padding: "6px 11px", borderRadius: "8px", border: active ? "1.5px solid #2F7D52" : "1px solid rgba(46,100,68,0.15)",
  background: active ? "#E6F4EC" : "#FFFFFF", color: active ? "#2F7D52" : "#5A7264", fontSize: "11px", fontWeight: 600,
  cursor: "pointer", fontFamily: "Manrope, sans-serif", display: "flex", alignItems: "center", gap: "5px",
});

/**
 * Full-screen 3D preview of the current design. Orbit with the mouse
 * (or one finger), zoom with the wheel (or pinch), pan with shift-drag /
 * right-drag (or two fingers).
 */
const SUN_OPTIONS = [["off", "Shadows off", null], ["am", "9 am", -45], ["noon", "Noon", 0], ["pm", "3 pm", 45]];

export default function Layout3DModal({ open, onClose, roofPolygons, roofSpecs, obstaclePolygons, obstacleHeights, panels, removedKeys, spec, designName, totals, latitude }) {
  const canvasRef = useRef(null);
  const camRef = useRef(null);
  const rafRef = useRef(0);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const [preset, setPreset] = useState("iso");
  const [labels, setLabels] = useState(true);
  const [sunKey, setSunKey] = useState("noon");
  const sunHourAngle = SUN_OPTIONS.find(o => o[0] === sunKey)?.[2] ?? null;
  const labelsRef = useRef(true);
  labelsRef.current = labels;

  const scene = useMemo(() => open ? buildScene({ roofPolygons, roofSpecs, obstaclePolygons, obstacleHeights, panels, removedKeys, spec, sunHourAngle, latitude }) : null,
    [open, roofPolygons, roofSpecs, obstaclePolygons, obstacleHeights, panels, removedKeys, spec, sunHourAngle, latitude]);
  // a sun change must not re-frame the camera; a preset click (or first open) must
  const framedPresetRef = useRef(null);

  const draw = useCallback(() => {
    rafRef.current = 0;
    const canvas = canvasRef.current;
    if (!canvas || !scene || !camRef.current) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) { canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderScene(ctx, scene, camRef.current, w, h, { labels: labelsRef.current });
  }, [scene]);   // labels read through a ref so toggling them never re-frames the camera
  const schedule = useCallback(() => { if (!rafRef.current) rafRef.current = requestAnimationFrame(draw); }, [draw]);

  // (re)frame when the scene or preset changes
  useEffect(() => {
    if (!scene) return;
    if (!camRef.current || framedPresetRef.current !== preset) { camRef.current = defaultCamera(scene, preset); framedPresetRef.current = preset; }
    schedule();
  }, [scene, preset, schedule]);
  useEffect(() => { if (!open) { framedPresetRef.current = null; camRef.current = null; } }, [open]);
  useEffect(() => { schedule(); }, [labels, schedule]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => schedule();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", onResize); window.removeEventListener("keydown", onKey);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0; pointers.current.clear(); gesture.current = null;   // a close mid-drag must not poison the next session
    };
  }, [open, onClose, schedule]);

  // ── pointer interaction ───────────────────────────────────────────────────
  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const cam = camRef.current; if (!cam) return;
    if (pointers.current.size === 1) {
      gesture.current = { mode: (e.button === 2 || e.shiftKey || e.button === 1) ? "pan" : "orbit", x: e.clientX, y: e.clientY, cam: { ...cam, target: { ...cam.target } } };
    } else if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { mode: "pinch", dist: Math.hypot(a.x - b.x, a.y - b.y), mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, cam: { ...cam, target: { ...cam.target } } };
    }
  };
  const panBy = (cam, base, dx, dy) => {
    // move the target in the camera's right/up plane; scale by distance so it feels constant
    const k = base.dist * 0.0018;
    const cp = Math.cos(base.yaw), sp = Math.sin(base.yaw);
    // right = (cos yaw, sin yaw, 0); up ≈ (−sin yaw·sin pitch, cos yaw·sin pitch, cos pitch)
    const r = { x: cp, y: sp, z: 0 };
    const u = { x: -sp * Math.sin(base.pitch), y: cp * Math.sin(base.pitch), z: Math.cos(base.pitch) };
    cam.target = { x: base.target.x - (r.x * dx - u.x * dy) * k, y: base.target.y - (r.y * dx - u.y * dy) * k, z: base.target.z - (r.z * dx - u.z * dy) * k };
  };
  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesture.current, cam = camRef.current; if (!g || !cam) return;
    if (g.mode === "pinch" && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      cam.dist = Math.max(2, Math.min(3000, g.cam.dist * (g.dist / Math.max(1, d))));
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      panBy(cam, g.cam, mid.x - g.mid.x, mid.y - g.mid.y);
    } else if (g.mode === "orbit") {
      cam.yaw = g.cam.yaw + (e.clientX - g.x) * 0.008;
      cam.pitch = Math.max(4 * DEG, Math.min(89 * DEG, g.cam.pitch + (e.clientY - g.y) * 0.006));
    } else if (g.mode === "pan") {
      panBy(cam, g.cam, e.clientX - g.x, e.clientY - g.y);
    }
    schedule();
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) gesture.current = null;
    else if (pointers.current.size === 1) { const [p] = [...pointers.current.values()]; const cam = camRef.current; gesture.current = { mode: "orbit", x: p.x, y: p.y, cam: { ...cam, target: { ...cam.target } } }; }
  };
  const onWheel = (e) => {
    const cam = camRef.current; if (!cam) return;
    e.preventDefault();
    cam.dist = Math.max(2, Math.min(3000, cam.dist * Math.pow(1.0015, e.deltaY)));
    schedule();
  };
  // React attaches wheel listeners passively; use a native one so preventDefault works
  useEffect(() => {
    const el = canvasRef.current; if (!el || !open) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scene, schedule]);

  const downloadPng = () => {
    const canvas = canvasRef.current; if (!canvas) return;
    const a = document.createElement("a");
    a.download = `${(designName || "solar-design").replace(/\s+/g, "-").toLowerCase()}-3d.png`;
    a.href = canvas.toDataURL("image/png");
    a.click();
  };

  if (!open) return null;
  return (
    <div data-testid="layout-3d-modal" style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,23,42,0.75)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ width: "min(1200px, 100%)", height: "min(820px, 100%)", background: "#FFFFFF", borderRadius: "14px", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 24px 64px rgba(0,0,0,0.35)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 14px", borderBottom: "1px solid rgba(46,100,68,0.1)", flexWrap: "wrap" }}>
          <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#0A1128", display: "flex", alignItems: "center", justifyContent: "center" }}><Box size={14} style={{ color: "#FFB703" }} /></div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#1B2B22", fontFamily: "Outfit, sans-serif", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{designName || "Solar Design"} — 3D view</div>
            <div style={{ fontSize: "10.5px", color: "#8AA496" }}>
              {totals ? `${totals.totalPanels} panels · ${totals.totalKwp} kWp · ` : ""}tilt {spec?.tiltDeg ?? "—"}° · azimuth {spec?.azimuthDeg ?? "—"}° · {roofPolygons.length} roof{roofPolygons.length === 1 ? "" : "s"}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {[["iso", "Isometric"], ["south", "From south"], ["north", "From north"], ["top", "Top"]].map(([k, l]) => (
              <button key={k} style={btn(preset === k)} data-testid={`view3d-${k}`} onClick={() => { if (preset === k && scene) { camRef.current = defaultCamera(scene, k); framedPresetRef.current = k; schedule(); } else setPreset(k); }}>{l}</button>
            ))}
            <select value={sunKey} onChange={e => setSunKey(e.target.value)} data-testid="view3d-sun" style={{ ...btn(sunKey !== "off"), paddingRight: "8px" }} title="Winter-solstice sun position for shadows">
              {SUN_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l === "Shadows off" ? l : `Winter sun · ${l}`}</option>)}
            </select>
            <button style={btn(labels)} onClick={() => setLabels(v => !v)} data-testid="view3d-labels">Labels</button>
            <button style={btn(false)} onClick={() => { if (scene) { camRef.current = defaultCamera(scene, preset); framedPresetRef.current = preset; schedule(); } }} title="Reset view"><RotateCcw size={12} /> Reset</button>
            <button style={btn(false)} onClick={downloadPng} title="Save as image" data-testid="view3d-download"><Download size={12} /> PNG</button>
            <button onClick={onClose} data-testid="view3d-close" style={{ ...btn(false), background: "#F5F8F5" }}><X size={13} /></button>
          </div>
        </div>
        <div style={{ position: "relative", flex: 1, minHeight: 0, background: "#EAF2FB" }}>
          {scene ? (
            <canvas ref={canvasRef} data-testid="layout-3d-canvas"
              onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
              onContextMenu={e => e.preventDefault()}
              style={{ width: "100%", height: "100%", display: "block", touchAction: "none", cursor: "grab" }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#8AA496", fontSize: "13px", fontFamily: "Manrope, sans-serif" }}>Draw a roof first to see it in 3D.</div>
          )}
          <div style={{ position: "absolute", top: "10px", left: "10px", background: "rgba(255,255,255,0.9)", borderRadius: "8px", padding: "6px 10px", fontSize: "10.5px", color: "#5A7264", fontFamily: "Manrope, sans-serif", pointerEvents: "none", border: "1px solid rgba(46,100,68,0.1)" }}>
            Drag to orbit · Scroll to zoom · Shift-drag / right-drag to pan · Esc to close
          </div>
          <div style={{ position: "absolute", bottom: "10px", right: "10px", background: "rgba(255,255,255,0.92)", borderRadius: "8px", padding: "8px 10px", fontSize: "10.5px", fontFamily: "Manrope, sans-serif", border: "1px solid rgba(46,100,68,0.1)", pointerEvents: "none" }}>
            {scene?.shading && <div style={{ marginBottom: "6px", fontSize: "11px", fontWeight: 700, color: scene.shading.shadedPanels ? "#B45309" : "#166534" }} data-testid="view3d-shading">
              {scene.shading.shadedPanels ? `${scene.shading.shadedPanels} panel${scene.shading.shadedPanels > 1 ? "s" : ""} shaded by the row in front (worst ${Math.round(scene.shading.worst * 100)}%)` : "No inter-row shading at this sun position"}
            </div>}
            {[["#1C429C", "Panel (tilted)"], ["#ECDEC4", "Roof"], ["#E8786C", "Obstacle (casts shadow)"], ["#969CA4", "Keep-out"], ["#1E293B", "Shadow (winter solstice)"], ["#F59E0B", "Sun / azimuth"]].map(([c, l]) => (
              <div key={l} style={{ display: "flex", alignItems: "center", gap: "6px", color: "#1B2B22", marginBottom: "3px" }}>
                <span style={{ width: "12px", height: "8px", background: c, borderRadius: "2px", display: "inline-block", border: "1px solid rgba(0,0,0,0.15)" }} />{l}
              </div>
            ))}
            <div style={{ color: "#8AA496", marginTop: "4px" }}>Grid {scene?.gridStep || 5} m · heights from Roof Height / Parapet / obstacle height. Preliminary visual only.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
